import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Avatar,
  Divider,
  Button,
  Spinner,
  Center,
  Heading,
  Select,
  useColorModeValue,
  useToast,
  Badge,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';

// Import service functions
import {
  fetchGroupDetails,
  fetchAllBoards,
  assignBoardToGroup,
  updateGroupMember,
} from '../ProjectBoardServices/AddGroupServices/AddGroupAPIs'; // Adjust path if needed

const AddGroupViewPage = () => {
  const { groupCode } = useParams();
  const history = useHistory();
  const [groupData, setGroupData] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [quotas, setQuotas] = useState({});
  const [managerId, setManagerId] = useState(null);
  const [updating, setUpdating] = useState({});
  const toast = useToast();

  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('blue.500', 'blue.700');
  const tableHoverBg = useColorModeValue('gray.50', 'gray.700');
  const nameColor = useColorModeValue('gray.900', 'white');

  const loadGroupDetails = async () => {
    setLoading(true);
    try {
      const data = await fetchGroupDetails(groupCode);

      if (!data.status || !data.data) {
        toast({ title: 'Group not found', status: 'warning' });
        return;
      }

      setGroupData(data.data);
      if (data.data.board?.id) {
        setSelectedBoardId(data.data.board.id);
      }

      const initialQuotas = {};
      let currentManager = null;

      Object.values(data.data.members || {}).forEach((memberObj) => {
        const memberId = memberObj.member_id;
        if (!memberId) return;

        const oldQuota = Number(memberObj.old_task_limit || 0);
        const newQuota = Number(memberObj.new_task_limit || oldQuota);

        initialQuotas[memberId] = { oldQuota, newQuota };

        if (Number(memberObj.is_manager) === 1) {
          currentManager = memberId;
        }
      });

      setQuotas(initialQuotas);
      setManagerId(currentManager);
    } catch (err) {
      toast({ title: 'Failed to load group', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadBoards = async () => {
    try {
      const data = await fetchAllBoards();
      if (data.status && Array.isArray(data.data)) {
        setBoards(data.data);
      }
    } catch (err) {
      console.error('Failed to load boards');
    }
  };

  const handleAssignBoard = async () => {
    if (!selectedBoardId) return;
    setAssigning(true);
    try {
      const data = await assignBoardToGroup(groupCode, selectedBoardId);

      if (data.status) {
        toast({ title: 'Board assigned successfully', status: 'success' });
        loadGroupDetails();
      } else {
        toast({ title: 'Assignment failed', description: data.message, status: 'error' });
      }
    } catch (err) {
      toast({ title: 'Network error', status: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    if (groupCode) {
      loadGroupDetails();
      loadBoards();
    }
  }, [groupCode]);

  const handleManagerCheckboxChange = (memberId) => {
    if (managerId && managerId !== memberId) {
      toast({
        title: "Note",
        description: "Changing manager ! previous one will be replaced.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    }
    setManagerId((prev) => (prev === memberId ? null : memberId));
  };

  const handleQuotaChange = (memberId, field, value) => {
    setQuotas((prev) => ({
      ...prev,
      [memberId]: {
        ...prev[memberId] || { oldQuota: 0, newQuota: 0 },
        [field]: value === '' ? 0 : Number(value),
      },
    }));
  };

  const handleUpdateMember = async (memberId) => {
    const quotaData = quotas[memberId] || { oldQuota: 0, newQuota: 0 };
    const payload = {
      group_code: groupCode,
      is_manager: managerId === memberId ? '1' : '0',
      old_card_limit: quotaData.oldQuota,
      new_card_limit: quotaData.newQuota,
    };

    setUpdating((prev) => ({ ...prev, [memberId]: true }));

    try {
      const data = await updateGroupMember(memberId, payload);

      if (data.status) {
        toast({ title: 'Member updated successfully', status: 'success' });

        setQuotas((prev) => ({
          ...prev,
          [memberId]: {
            oldQuota: payload.old_card_limit,
            newQuota: payload.new_card_limit,
          },
        }));

        if (payload.is_manager === '0' && managerId === memberId) {
          setManagerId(null);
        }
      } else {
        toast({
          title: 'Update failed',
          description: data.message || 'Unknown error',
          status: 'error',
        });
      }
    } catch (err) {
      toast({ title: 'Network error', status: 'error' });
    } finally {
      setUpdating((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  if (loading) {
    return (
      <Center minH="80vh">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" thickness="4px" />
          <Text color={muted}>Loading group details...</Text>
        </VStack>
      </Center>
    );
  }

  if (!groupData) {
    return (
      <Center minH="80vh">
        <VStack spacing={6}>
          <Text fontSize="xl" color={muted}>Group not found</Text>
          <Button leftIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
            Go Back
          </Button>
        </VStack>
      </Center>
    );
  }

  const currentBoard = groupData.board;
  const membersArray = Object.values(groupData.members || {}).map((memberObj) => ({
    ...memberObj,
    memberId: memberObj.member_id,
    details: memberObj.details || {},
  })).filter(m => m.memberId);

  return (
    <Box bg={bg} minH="100vh" py={10}>
      <Container maxW="container.lg">
        <VStack spacing={10} align="stretch">

          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="ghost"
              colorScheme="blue"
              onClick={() => history.goBack()}
            >
              Back to Groups
            </Button>
            <Heading size="xl" color="blue.600">
              {groupData.name}
            </Heading>
            <Badge colorScheme="purple" fontSize="lg" px={4} py={1}>
              {groupData.group_code}
            </Badge>
          </Flex>

          <Divider />

          {/* Assigned Board Section */}
          <Box bg={cardBg} p={5} borderRadius="lg" shadow="sm" border="1px solid" borderColor={borderColor}>
            <VStack align="stretch" spacing={4}>
              <Heading size="sm" color="gray.700">Assigned Board</Heading>

              {currentBoard ? (
                <HStack spacing={3} align="center">
                  <Box w="10" h="10" borderRadius="md" bg={currentBoard.backgroundColor || 'gray.400'} />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" fontSize="md" color="black">{currentBoard.title}</Text>
                    <Text fontSize="sm" color={muted}>{currentBoard.description || 'No description'}</Text>
                  </VStack>
                </HStack>
              ) : (
                <Text fontSize="sm" color={muted} fontStyle="italic">No board assigned yet</Text>
              )}

              <HStack spacing={3} align="center">
                <Select
                  placeholder="Select board"
                  value={selectedBoardId}
                  onChange={(e) => setSelectedBoardId(e.target.value)}
                  size="sm"
                  flex={1}
                >
                  {boards.map((board) => (
                    <option key={board.id} value={board.id}>{board.title}</option>
                  ))}
                </Select>
                <Button
                  colorScheme="blue"
                  size="sm"
                  leftIcon={<CheckIcon />}
                  onClick={handleAssignBoard}
                  isLoading={assigning}
                  isDisabled={!selectedBoardId || selectedBoardId === currentBoard?.id}
                >
                  Assign
                </Button>
              </HStack>
            </VStack>
          </Box>

          {/* Team Members Table */}
          <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px solid" borderColor={borderColor}>
            <Heading size="md" mb={6}>
              Team Members ({membersArray.length})
            </Heading>

            {membersArray.length > 0 ? (
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th color="white">#</Th>
                      <Th color="white">Name</Th>
                      <Th color="white">Manager</Th>
                      <Th color="white">Old Quota</Th>
                      <Th color="white">New Quota</Th>
                      <Th color="white" textAlign="center">Save</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {membersArray.map((member, index) => {
                      const { memberId, details } = member;
                      const q = quotas[memberId] || { oldQuota: 0, newQuota: 0 };

                      // Agar value 0 hai to placeholder dikhana hai
                      const oldQuotaDisplay = q.oldQuota === 0 ? '' : q.oldQuota;
                      const newQuotaDisplay = q.newQuota === 0 ? '' : q.newQuota;

                      return (
                        <Tr key={memberId} _hover={{ bg: tableHoverBg }} transition="background 0.2s">
                          <Td>{index + 1}</Td>
                          <Td>
                            <HStack spacing={3}>
                              <Avatar
                                size="sm"
                                name={`${details.admin_firstname || 'U'} ${details.admin_lastname || ''}`}
                              />
                              <Text fontWeight="medium" color={nameColor}>
                                {details.admin_firstname || 'Unknown'} {details.admin_lastname || ''}
                              </Text>
                            </HStack>
                          </Td>
                          <Td>
                            <Checkbox
                              isChecked={managerId === memberId}
                              onChange={() => handleManagerCheckboxChange(memberId)}
                              colorScheme="blue"
                            />
                          </Td>
                          <Td>
                            <NumberInput
                              value={oldQuotaDisplay}
                              onChange={(val) => handleQuotaChange(memberId, 'oldQuota', val)}
                              min={0}
                              size="sm"
                              placeholder="0"
                            >
                              <NumberInputField placeholder="0" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td>
                            <NumberInput
                              value={newQuotaDisplay}
                              onChange={(val) => handleQuotaChange(memberId, 'newQuota', val)}
                              min={0}
                              size="sm"
                              placeholder="0"
                            >
                              <NumberInputField placeholder="0" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td textAlign="center">
                            <Button
                              size="sm"
                              colorScheme="green"
                              isLoading={updating[memberId]}
                              onClick={() => handleUpdateMember(memberId)}
                            >
                              Save
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Center py={10}>
                <Text color={muted} fontStyle="italic">No members in this group yet</Text>
              </Center>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default AddGroupViewPage;